import React, { useState, useEffect, useRef, useMemo } from 'react';

/**
 * 🔥 Безопасное извлечение URL изображения из любых структур данных MongoDB/Cloudinary
 */
const extractImageUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  if (typeof img === 'object') {
    return img.url || img.secure_url || img.src || img.path || img.link || '';
  }
  return '';
};

export const getOptimizedUrl = (urlInput, width = 1000, quality = 80, blur = false) => {
  const url = extractImageUrl(urlInput);
  if (!url || typeof url !== 'string' || !url.startsWith('http')) return url || '';

  if (url.includes('res.cloudinary.com')) {
    const uploadIndex = url.indexOf('/upload/');
    if (uploadIndex === -1) return url;
    let transformations = `f_auto,q_auto`;
    if (width) transformations += `,w_${width}`;
    if (blur) transformations += `,e_blur:1000,w_40`;
    return `${url.slice(0, uploadIndex)}/upload/${transformations}${url.slice(uploadIndex + 7)}`;
  }

  if (url.includes('images.unsplash.com')) {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.set('auto', 'format');
      urlObj.searchParams.set('fit', 'crop');
      urlObj.searchParams.set('w', width.toString());
      urlObj.searchParams.set('q', blur ? '10' : quality.toString());
      if (blur) urlObj.searchParams.set('blur', '100');
      return urlObj.toString();
    } catch (e) {
      return url;
    }
  }

  return url;
};

export const OptimizedImage = ({ src, alt = '', className = '', containerClassName = '', aspect = 'aspect-square' }) => {
  const rawSrc = extractImageUrl(src);
  const [currentDisplaySrc, setCurrentDisplaySrc] = useState(rawSrc);
  const [isLoaded, setIsLoaded] = useState(false);

  const optimizedSrc = getOptimizedUrl(rawSrc, 1200, 85, false);
  const altStr = typeof alt === 'object' ? String(Object.values(alt)[0] || '') : String(alt || '');

  useEffect(() => {
    if (!rawSrc) return;
    
    const img = new Image();
    img.src = optimizedSrc;
    img.onload = () => {
      setCurrentDisplaySrc(optimizedSrc);
      setIsLoaded(true);
    };
  }, [rawSrc, optimizedSrc]);

  return (
    <div className={`relative overflow-hidden bg-neutral-100 ${aspect} ${containerClassName} select-none`}>
      {currentDisplaySrc ? (
        <img
          src={currentDisplaySrc}
          alt={altStr}
          className={`w-full h-full object-cover transition-opacity duration-200 pointer-events-none select-none ${
            isLoaded ? 'opacity-100' : 'opacity-90'
          } ${className}`}
          draggable={false}
        />
      ) : (
        <div className="w-full h-full bg-neutral-100" />
      )}
    </div>
  );
};

export const ProductGallery = ({ images = [], alt = '' }) => {
  const [selected, setSelected] = useState(0);
  const scrollRef = useRef(null);

  // Состояния для отслеживания свайпа
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const minSwipeDistance = 40;

  // Очистка и проверка массива картинок
  const normalizedImages = useMemo(() => {
    return Array.isArray(images)
      ? images.map((img) => extractImageUrl(img)).filter(Boolean)
      : [];
  }, [images]);

  const altStr = typeof alt === 'object' ? String(Object.values(alt)[0] || '') : String(alt || '');

  // Переключение ракурсов
  const handleNext = () => {
    if (normalizedImages.length <= 1) return;
    setSelected((prev) => (prev + 1) % normalizedImages.length);
  };

  const handlePrev = () => {
    if (normalizedImages.length <= 1) return;
    setSelected((prev) => (prev - 1 + normalizedImages.length) % normalizedImages.length);
  };

  // Обработка свайпов пальцем
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }
  };

  // Автоматический скролл ленты миниатюр к активному фото
  useEffect(() => {
    if (scrollRef.current && normalizedImages.length > 1) {
      const activeThumb = scrollRef.current.children[selected];
      if (activeThumb) {
        activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selected, normalizedImages.length]);

  // Предзагрузка всех картинок в кэш браузера
  useEffect(() => {
    if (normalizedImages.length > 0) {
      normalizedImages.forEach((url) => {
        if (url) {
          const preloadImg = new Image();
          preloadImg.src = getOptimizedUrl(url, 1200, 85);
        }
      });
    }
  }, [normalizedImages]);

  // Плавный горизонтальный скролл колёсиком мыши
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleWheelScroll = (e) => {
      if (el.scrollWidth > el.clientWidth) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };

    el.addEventListener('wheel', handleWheelScroll, { passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheelScroll);
    };
  }, [images]);

  if (!normalizedImages.length) {
    return <div className="aspect-square bg-neutral-100 w-full select-none" />;
  }

  const activeImageSrc = normalizedImages[selected] || normalizedImages[0] || '';

  return (
    <div className="w-full select-none">
      {/* Главное изображение с поддержкой свайпов и клик-зон */}
      <div 
        className="mb-4 aspect-square overflow-hidden bg-neutral-100 relative rounded-none select-none cursor-pointer"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <OptimizedImage
          src={activeImageSrc}
          alt={altStr}
          aspect="aspect-square"
          className="w-full h-full object-cover"
        />

        {/* Клик-зоны для ПК и планшетов (Левая и Правая половины) */}
        {normalizedImages.length > 1 && (
          <>
            <div
              onClick={handlePrev}
              className="absolute left-0 top-0 w-1/2 h-full z-10"
              title="Previous image"
            />
            <div
              onClick={handleNext}
              className="absolute right-0 top-0 w-1/2 h-full z-10"
              title="Next image"
            />
          </>
        )}
      </div>

      {/* Лента миниатюр снизу */}
      {normalizedImages.length > 1 && (
        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto py-1 thumb-scrollbar select-none"
        >
          {normalizedImages.map((imgUrl, index) => {
            const thumbSrc = getOptimizedUrl(imgUrl, 160, 80);
            return (
              <button
                key={imgUrl || `thumb-${index}`}
                type="button"
                onClick={() => setSelected(index)}
                className={`w-20 h-20 overflow-hidden bg-neutral-100 flex-shrink-0 transition-all rounded-none select-none focus:outline-none ${
                  selected === index ? 'ring-2 ring-black opacity-100' : 'opacity-60 hover:opacity-100'
                }`}
                data-testid={`thumbnail-${index}`}
              >
                <img
                  src={thumbSrc}
                  alt={`${altStr} preview ${index + 1}`}
                  className="w-full h-full object-cover pointer-events-none select-none"
                  loading="lazy"
                  draggable={false}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductGallery;
